import { components } from "@/api-schema";
import { GroupWithAppRoleTable } from "@/components/data-table/predefined/group-with-app-role-table";
import { groupWithAppRoleTableDefaultColumns } from "@/components/data-table/predefined/group-with-app-role-table-columns";
import { DataTableHeightMode, TableBackground } from "@/components/data-table/types";
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import i18next from "i18next";
import { useEffect } from "react";
import { UseFormReturn, useFormState } from "react-hook-form";
import { useLocation } from "wouter";
import { AppRoleFormSchemaType, isAppRoleSaveDisabled } from "../_helpers/app-role-form.helpers";
import { useAppRoleGroupSelection } from "../_hooks/use-app-role-group-selection";
import { AppRoleGroupTableFooter } from "./app-role-group-table-footer";

type GroupWithAppRole = components["schemas"]["GroupWithAppRoleResponseDto"];

export interface AppRoleGroupPageData {
  data: GroupWithAppRole[];
  meta?: { total?: number; totalSelected?: number };
}

const GROUP_COLUMNS = [groupWithAppRoleTableDefaultColumns.columnName];

interface AppRoleFormFieldsProps {
  formId: string;
  form: UseFormReturn<AppRoleFormSchemaType>;
  pageData: AppRoleGroupPageData | undefined;
  isLoading: boolean;
  page: number;
  pageSize: number;
  isNameDisabled?: boolean;
  onSearchChange: (term: string) => void;
  onPaginationChange: (pagination: { page: number; pageSize: number }) => void;
  onSave: (groupUuidsToAdd: string[], groupUuidsToRemove: string[]) => Promise<void>;
  onSaveDisabledChange: (disabled: boolean) => void;
}

export function AppRoleFormFields({
  formId,
  form,
  pageData,
  isLoading,
  page,
  pageSize,
  isNameDisabled = false,
  onSearchChange,
  onPaginationChange,
  onSave,
  onSaveDisabledChange,
}: AppRoleFormFieldsProps) {
  const {
    currentPageSelectedGroups,
    groupUuidsToAdd,
    groupUuidsToRemove,
    handleSelectionChange,
    handleClearSelection,
    handleRowClick,
  } = useAppRoleGroupSelection(pageData?.data);

  const [, navigate] = useLocation();
  const { isValid, isDirty } = useFormState({ control: form.control });

  const totalSelectedCount = (pageData?.meta?.totalSelected ?? 0) + groupUuidsToAdd.length - groupUuidsToRemove.length;
  const isSelectionDirty = groupUuidsToAdd.length > 0 || groupUuidsToRemove.length > 0;
  const isSaveDisabled = isAppRoleSaveDisabled({ isValid, isDirty, hasGroupChanges: isSelectionDirty });

  useEffect(() => {
    onSaveDisabledChange(isSaveDisabled);
  }, [onSaveDisabledChange, isSaveDisabled]);

  return (
    <form
      id={formId}
      className="flex h-full flex-col space-y-6"
      onSubmit={(e) => {
        e.preventDefault();
        void onSave(groupUuidsToAdd, groupUuidsToRemove);
      }}
    >
      <FormField
        control={form.control}
        name="name"
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel>{i18next.t("common:Name")}</FormLabel>
            <FormControl>
              <Input {...field} disabled={isNameDisabled} />
            </FormControl>
            {!isNameDisabled && !fieldState.error && (
              <FormDescription>{i18next.t("ram:TheNameMustComplyWithConventionsForNaming")}</FormDescription>
            )}
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="flex min-h-0 flex-1 flex-col">
        <FormLabel className="mb-0">{i18next.t("ram:Groups")}</FormLabel>
        <GroupWithAppRoleTable
          tableBackground={TableBackground.Card}
          columns={GROUP_COLUMNS}
          autoFetch={false}
          data={pageData?.data ?? []}
          isLoading={isLoading}
          page={page}
          pageSize={pageSize}
          totalItems={pageData?.meta?.total}
          onPaginationChange={onPaginationChange}
          onSearchChange={onSearchChange}
          features={{
            multiSelection: true,
            pagination: true,
            search: true,
          }}
          selection={currentPageSelectedGroups}
          selectionMode="object"
          onSelectionChange={handleSelectionChange}
          onClearSelection={handleClearSelection}
          onRowClick={handleRowClick}
          heightMode={DataTableHeightMode.Content}
          searchPlaceholder={i18next.t("ram:SearchForGroup")}
          footerComponent={
            <AppRoleGroupTableFooter totalSelectedCount={totalSelectedCount} onClearSelection={handleClearSelection} />
          }
          showClearSelection={false}
          showCreateButton={false}
          onView={(group) => navigate(`~/ram/group/${group.uuid}`)}
          defaultRowActions={{
            showView: true,
            showEdit: false,
            showDelete: false,
          }}
        />
      </div>
    </form>
  );
}
